import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEligibleStorageMain } from '../../hooks/useEligibleStorageMain';
import { useBlockedStoragesMain } from '../../hooks/useBlockedStoragesMain';
import { useCreateStoragePreInvoice } from '../../hooks/useCreateStoragePreInvoice';
import { useBulkInvoiceStoragesByFilters } from '../../hooks/useBulkInvoiceStoragesByFilters';
import { useBlockStorage } from '../../hooks/useBlockStorage';
import { useUnblockStorage } from '../../hooks/useUnblockStorage';
import { useAgGridColumns, useBlockedStoragesColumns } from '../../hooks/useAgGridColumns';
import { getEligibleStorageFilterOptions } from '../../config/filterOptions';
import { EligibleStorageAgGridTable, BlockedStoragesAgGridTable, Breadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, SelectionToolbar, PrefacturarButton, EligibleStorageDropdownActions } from '../interaction';
import { PrefacturarModal, PrefacturarModalRef, BlockStorageModal, BlockStorageModalRef } from '../modals';
import { EligibleStorage } from '../../models/EligibleStorage';
import { useTranslation } from 'react-i18next';

export const Main = () => {
  const { t } = useTranslation('eligibleStorageModule');
  const prefacturarModalRef = useRef<PrefacturarModalRef>(null);
  const blockStorageModalRef = useRef<BlockStorageModalRef>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedStorages, setSelectedStorages] = useState<EligibleStorage[]>([]);

  // Estado para el tab activo
  const [activeTab, setActiveTab] = useState<'eligible' | 'blocked'>('eligible');

  const {
    storages,
    isLoading,
    isFetching,
    error,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
    filterConditions,
    searchTerm,
    selectedColumns,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
  } = useEligibleStorageMain();

  const createPreInvoiceMutation = useCreateStoragePreInvoice();
  const { bulkInvoiceByFilters, isInvoicing } = useBulkInvoiceStoragesByFilters();
  const { blockStorage } = useBlockStorage();
  const { unblockStorage } = useUnblockStorage();

  const filterOptions = useMemo(() => getEligibleStorageFilterOptions(t), [t]);

  // Hook que maneja toda la lógica de negocio de storages bloqueados
  const {
    // Estados
    selectedColumns: blockedSelectedColumns,
    sorting: blockedSorting,

    // Datos
    blockedStorages,
    isLoading: blockedIsLoading,
    isFetching: blockedIsFetching,
    error: blockedError,
    total: blockedTotal,
    refetch: blockedRefetch,

    // Props para infinite scroll
    fetchNextPage: blockedFetchNextPage,
    hasNextPage: blockedHasNextPage,
    isFetchingNextPage: blockedIsFetchingNextPage,

    // Handlers
    handleFilterChange: blockedHandleFilterChange,
    handleSearch: blockedHandleSearch,
    handleColumnsChange: blockedHandleColumnsChange,
    handleSortingChange: blockedHandleSortingChange,

    // Estado de filtros
    hasActiveFilters: blockedHasActiveFilters,
    filterConditions: blockedFilterConditions,
    searchTerm: blockedSearchTerm,
  } = useBlockedStoragesMain();

  const handleSelectionChanged = useCallback((selected: EligibleStorage[]) => {
    setSelectedStorages(selected);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedStorages([]);
  }, []);

  const handleStorageAction = useCallback(
    (action: 'removeFromPreinvoice' | 'block', storage: EligibleStorage) => {
      if (action === 'block') {
        blockStorageModalRef.current?.open(storage.id, (data) => {
          blockStorage(
            storage.id,
            () => {
              // Recargar el listado de elegibles (el storage desaparecerá de aquí)
              refetch();
              // También recargar bloqueados por si el usuario cambia de tab
              blockedRefetch();
            },
            {
              storage_block_reason_id: data.storage_block_reason_id,
              comment: data.comment,
            },
          );
        });
      }
    },
    [blockStorage, refetch, blockedRefetch],
  );

  const handleCreatePreInvoice = useCallback(() => {
    if (selectedStorages.length === 0) return;

    const storageIds = selectedStorages.map((s) => s.id);
    createPreInvoiceMutation.mutate(storageIds, {
      onSuccess: () => {
        setSelectedStorages([]);
      },
    });
  }, [selectedStorages, createPreInvoiceMutation]);

  const handlePrefacturar = useCallback(() => {
    // Abrir modal con los datos de filtros
    prefacturarModalRef.current?.open({
      totalResults: total, // 🎯 Total de storages según filtros aplicados
      filters: filterConditions,
      filterOptions: filterOptions || [],
      onConfirm: () => {
        // 🎯 Convertir filterConditions a formato advanced_filters
        const advancedFilters =
          hasActiveFilters && filterConditions.length > 0
            ? {
                filters: filterConditions.map((condition) => {
                  const operator = condition.action;
                  let value: any = condition.value;

                  // 🎯 Los operadores 'in' y 'not_in' requieren valores en array
                  if (operator === 'in' || operator === 'not_in') {
                    if (!Array.isArray(value)) {
                      // Convertir string separado por comas a array
                      if (typeof value === 'string') {
                        value = value
                          .split(',')
                          .map((v: string) => v.trim())
                          .filter(Boolean);
                      } else {
                        value = [value];
                      }
                    }
                  }

                  return {
                    column: condition.column,
                    operator: operator,
                    value: value,
                  };
                }),
                logic: 'AND', // Por defecto usamos AND para los filtros
              }
            : {
                filters: [],
                logic: 'AND',
              };

        // 🎯 Llamar a la API de prefacturación por filtros
        bulkInvoiceByFilters(advancedFilters, () => {
          // Cerrar modal y refetch después del éxito
          prefacturarModalRef.current?.close();
          handleClearSelection();
          refetch();
        });
      },
    });
  }, [total, filterConditions, filterOptions, hasActiveFilters, bulkInvoiceByFilters, handleClearSelection, refetch]);

  const { filteredColumnDefs, tanStackColumns } = useAgGridColumns({
    selectedColumns,
    onStorageAction: handleStorageAction,
  });

  const { filteredColumnDefs: blockedFilteredColumnDefs, tanStackColumns: blockedTanStackColumns } =
    useBlockedStoragesColumns({
      selectedColumns: blockedSelectedColumns,
      onUnblockStorage: (blockId) => {
        unblockStorage(blockId, () => {
          // Recargar el listado de bloqueados (el storage desaparecerá de aquí)
          blockedRefetch();
          // También recargar elegibles (el storage volverá a aparecer aquí)
          refetch();
        });
      },
    });

  useEffect(() => {
    if (activeTab === 'eligible' && !isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad, activeTab]);

  useEffect(() => {
    if (activeTab === 'blocked' && !blockedIsLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [blockedIsLoading, isInitialLoad, activeTab]);

  if (isInitialLoad) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <>
      <Breadcrumb />
      <div className="px-6 pt-4">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'eligible' | 'blocked')}>
          <TabsList className="mb-4">
            <TabsTrigger value="eligible">{t('tabs.eligible', 'Elegibles')}</TabsTrigger>
            <TabsTrigger value="blocked">{t('tabs.blocked', 'Bloqueados')}</TabsTrigger>
          </TabsList>

          <TabsContent value="eligible">
            <PrefacturarButton onClick={handlePrefacturar} disabled={false} />
            {/* Header con filtros y botón crear */}
            <div className="mb-4">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <FiltersSection
                    handleSearch={handleSearch}
                    onFilterChange={handleFilterChange}
                    columns={tanStackColumns}
                    selectedColumns={selectedColumns}
                    onColumnsChange={handleColumnsChange}
                    filterConditions={filterConditions}
                    searchTerm={searchTerm}
                  />
                </div>
                <TotalResults total={total} />
              </div>
            </div>

            {!isLoading && storages.length === 0 && (
              <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
            )}

            {!isLoading && storages.length > 0 && (
              <div
                className={`transition-opacity duration-300 ${
                  isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
                }`}
              >
                <EligibleStorageAgGridTable
                  storages={storages}
                  columnDefs={filteredColumnDefs}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onSelectionChanged={handleSelectionChanged}
                />
              </div>
            )}

            <SelectionToolbar
              selectedCount={selectedStorages.length}
              onClose={handleClearSelection}
              onCreatePreInvoice={handleCreatePreInvoice}
              isCreating={createPreInvoiceMutation.isPending}
            />
          </TabsContent>

          <TabsContent value="blocked">
            {/* Header con filtros */}
            <div className="mb-4">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <FiltersSection
                    handleSearch={blockedHandleSearch}
                    onFilterChange={blockedHandleFilterChange}
                    columns={blockedTanStackColumns}
                    selectedColumns={blockedSelectedColumns}
                    onColumnsChange={blockedHandleColumnsChange}
                    filterConditions={blockedFilterConditions}
                    searchTerm={blockedSearchTerm}
                  />
                </div>
                <TotalResults total={blockedTotal} />
              </div>
            </div>

            {!blockedIsLoading && blockedStorages.length === 0 && (
              <EmptyView type={blockedHasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
            )}

            {!blockedIsLoading && blockedStorages.length > 0 && (
              <div
                className={`transition-opacity duration-300 ${
                  blockedIsFetching && !blockedIsFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
                }`}
              >
                <BlockedStoragesAgGridTable
                  blockedStorages={blockedStorages}
                  columnDefs={blockedFilteredColumnDefs}
                  selectedColumns={blockedSelectedColumns}
                  fetchNextPage={blockedFetchNextPage}
                  hasNextPage={blockedHasNextPage}
                  isFetchingNextPage={blockedIsFetchingNextPage}
                  sorting={blockedSorting}
                  onSortingChange={blockedHandleSortingChange}
                  onUnblockStorage={(blockId) => {
                    unblockStorage(blockId, () => {
                      blockedRefetch();
                    });
                  }}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <PrefacturarModal ref={prefacturarModalRef} />
      <BlockStorageModal ref={blockStorageModalRef} />
    </>
  );
};
